import React from 'react';

import classNames from 'classnames';
import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Badge from '@material-ui/core/Badge';
import grey from '@material-ui/core/colors/grey';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import withWidth, { isWidthDown, WithWidth } from '@material-ui/core/withWidth';

import { autosaveDraft } from '../../action/autosave/autosave-draft';
import { clearAutosavedDraft } from '../../action/autosave/clear-autosaved-draft';
import { addNotificationAction } from '../../action/notifications/notifications';
import { loadEmployeeComments } from '../../comments/actions/load-comments';
import { sendText } from '../../contacts/actions/send-text';
import {
  EMPLOYEE_STATUS,
  EmployeeStatus,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { loadEmployeeMessages } from '../../messages/actions/load-employee-messages';
import { updateEmployeeMessage } from '../../messages/actions/update-employee-message';
import { draftMessageForEmployeeSelector } from '../../reducers/autosave-selectors';
import { employeeContextSelector } from '../../reducers/contact-selectors';
import {
  employeesForOrgIdSelector,
  employeesStateForOrgIdAndAllStatusSelector,
} from '../../reducers/employee-selectors';
import {
  employeeMessagesForOrgIdSelector,
  employeeMessagesForOrgIdStatusSelector,
} from '../../reducers/message-selectors';
import { RequestState } from '../../reducers/utils/state-manager';

import { EmployeeInvalidPhoneNumberWarning } from '../../employees/employee-invalid-phone-number-warning';
import { EmployeeNotificationsWarning } from '../../employees/employee-notifications-warning';
import { EmployeeTextMessageList } from '../../employees/employee-text-message-list';
import { EmployeeProfileDialog } from '../../employees/profile/employee-profile-dialog';
import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';
import { UiBreadcrumbHeader } from '../../ui/ui-breadcrumb-header';
import { UiButton } from '../../ui/ui-button';
import * as WebRoutes from '../../web-routes';
import { EmployeeMessageComposer } from '../message-composer/employee-message-composer';
import { EmployeeMessageTemplates } from '../message-composer/employee-message-templates';
import { EmployeeMessagesEmptyState } from '../message-composer/employee-messages-empty-state';

import { EmployeeAddNewMessage } from './employee-add-new-message';

import { EmployeeNotesButton } from '../../employees/widgets/employee-notes-button';
import { AUTOSAVED_DRAFT_TYPES } from '../../util/autosaved-draft-types';
import { EmployeeCanMessage } from '../../util/can-message';
import { HTMLStripper } from '../../util/html-stripper';
import { LANGUAGES } from '../../util/languages';
import {
  HeaderButtonAddIcon,
  ICON_NAMES,
  MaterialDesignIcon,
} from '../../util/material-design-icons';
import { MESSAGE_TYPE, MessageType } from '../../util/message-types';
import { TEMPLATE_TYPE } from '../../util/template-type';
import { ContactMessageErrors } from '../../widgets/contact-message-errors';
import MessageWidget from '../../widgets/legacy-message';
import { LoadingMessage } from '../../widgets/loading-message';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    contacts: {
      width: '300px',
      border: `1px solid ${grey[300]}`,
      borderWidth: '1px 1px 0 0',
      overflowY: 'auto',
      flexShrink: 0,
    },
    contact: {
      borderBottom: `1px solid ${grey[300]}`,
      cursor: 'pointer',
      '&:hover': {
        background: grey[300],
      },
      '&.selected': {
        background: (theme as any).palette.primary[400],
        color: 'white',
      },
    },
    contactLink: {
      color: (theme as any).palette.secondary[400],
      fontWeight: 500,
      display: 'inline-block',
      '&:hover': {
        color: (theme as any).palette.secondary[600],
      },
      '&.selected': {
        color: 'white',
      },
    },
    categoryBanner: {
      padding: '4px 12px 4px 8px',
      textTransform: 'uppercase',
      fontWeight: 500,
      background: grey[300],
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    threadPane: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      background: 'white',
      borderTop: `1px solid ${grey[300]}`,
    },
    threadHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    threadContainer: {
      overflowY: 'auto',
      borderTop: `1px solid ${grey[300]}`,
      height: '100%',
      position: 'relative',
    },
    thread: {
      maxWidth: '800px',
      margin: '0px auto 120px auto',
    },
    messageBody: {
      fontSize: '12px',
      marginTop: '3px',
      wordBreak: 'break-word',
    },
    hideButton: {
      opacity: 0,
      ':hover > &': {
        opacity: 1,
      },
    },
    mobile: {
      width: '100%',
      flexShrink: 0,
    },
    hidden: {
      display: 'none',
    },
  });

const mapStateToProps = (
  state: any,
  { orgId, queryParams: { employeeId, messageThreadPreviewScrollPosition } }: InputProps,
) => {
  const activeEmployeeId = employeeId ? parseInt(employeeId, 10) : null;
  return {
    messages: employeeMessagesForOrgIdSelector(state, orgId),
    employees: employeesForOrgIdSelector(state, orgId),
    employeesStates: employeesStateForOrgIdAndAllStatusSelector(state, orgId),
    employeeOrgMessagesState: employeeMessagesForOrgIdStatusSelector(state, orgId),
    activeEmployeeId,
    messageThreadPreviewScrollPosition: messageThreadPreviewScrollPosition
      ? parseInt(messageThreadPreviewScrollPosition, 10)
      : null,
    messageDraft: activeEmployeeId
      ? draftMessageForEmployeeSelector(state, activeEmployeeId)
      : null,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    sendMessage: (
      contactId: number,
      context: ContactContextType,
      messageBody: string,
      messageType: string,
      subject?: string | null,
      sendDateTime?: Moment | null,
      urlMappings?: Record<string, string>,
      sendVCardForOrgId?: number,
    ) =>
      dispatch(
        sendText(
          contactId,
          context,
          messageBody,
          messageType,
          subject,
          { orgId },
          sendDateTime,
          urlMappings,
          sendVCardForOrgId,
        ),
      ),
    loadOrgEmployees: (employeeStatus: EmployeeStatus) =>
      dispatch(loadOrgEmployeesByStatus(orgId, employeeStatus)),
    loadEmployeeMessages: () => dispatch(loadEmployeeMessages(orgId)),
    updateEmployeeMessage: (
      contactId: number,
      messageId: number,
      employeeId: number,
      update: Partial<Message>,
    ) => dispatch(updateEmployeeMessage(orgId, contactId, messageId, employeeId, update)),
    showFailedUploadAction: (fileName: string) =>
      dispatch(
        addNotificationAction({
          key: `failed-upload-${fileName}`,
          message: 'File upload failed, please try again',
        }),
      ),
    loadEmployeeNotes: (employeeId: number) => dispatch(loadEmployeeComments({ employeeId })),
    clearSavedDraft: (employeeId: number) =>
      dispatch(clearAutosavedDraft(AUTOSAVED_DRAFT_TYPES.MESSAGE, { employeeId })),
    autosaveDraft: (
      employeeId: number,
      messageBody: string,
      messageType: MessageType,
      subject?: string,
    ) =>
      dispatch(
        autosaveDraft(AUTOSAVED_DRAFT_TYPES.MESSAGE, {
          context: { employeeId },
          messageBody,
          messageType,
          subject,
        }),
      ),
  },
});

type InputProps = {
  orgId: number;
  queryParams: {
    employeeId?: string;
    messageThreadPreviewScrollPosition?: string;
  };
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type Props = InputProps &
  StateProps &
  DispatchProps &
  WithWidth &
  WithStyles<typeof styles> &
  RouteComponentProps;

type State = {
  popupEmployeeId: number | null;
  selectingEmployee: boolean;
  messageType: MessageType | null;
  selectedTab: string | null;
};

class EmployeeMessagesComponent extends React.PureComponent<Props, State> {
  messageComposerRef: React.RefObject<HTMLDivElement>;

  messageThreadPreviewRef: React.RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);
    this.messageComposerRef = React.createRef();
    this.messageThreadPreviewRef = React.createRef();

    this.state = {
      popupEmployeeId: null,
      selectingEmployee: false,
      messageType: null,
      selectedTab: null,
    };
  }

  componentDidMount() {
    this.props.actions.loadEmployeeMessages().then(() => {
      if (this.props.messageThreadPreviewScrollPosition) {
        this.messageThreadPreviewRef.current?.scroll({
          top: this.props.messageThreadPreviewScrollPosition,
        });
      }
    });
    this.props.actions.loadOrgEmployees(EMPLOYEE_STATUS.CURRENT);
    this.props.actions.loadOrgEmployees(EMPLOYEE_STATUS.FORMER);
    this.setActiveEmployeeId();
  }

  componentDidUpdate(prevProps: Props) {
    this.props.actions.loadEmployeeMessages().then(() => {
      if (this.props.messageThreadPreviewScrollPosition) {
        this.messageThreadPreviewRef.current?.scroll({
          top: this.props.messageThreadPreviewScrollPosition,
        });
      }
    });
    this.props.actions.loadOrgEmployees(EMPLOYEE_STATUS.CURRENT);
    this.props.actions.loadOrgEmployees(EMPLOYEE_STATUS.FORMER);
    if (
      (!prevProps.messages && this.props.messages) ||
      (prevProps.activeEmployeeId && !this.props.activeEmployeeId)
    ) {
      this.setActiveEmployeeId();
    }
  }

  onSmallScreen = () => isWidthDown('sm', this.props.width);

  onMobile = () => isWidthDown('xs', this.props.width);

  setActiveEmployeeId = () => {
    const { activeEmployeeId, messages, orgId, history } = this.props;
    if (this.onSmallScreen() || !messages || activeEmployeeId) {
      return;
    }

    const unreadMessages = messages.filter((m) => m.unread);

    if (unreadMessages.length) {
      history.replace(
        WebRoutes.employeeMessagesRoute({
          orgId,
          employeeId: unreadMessages[0].employeeId,
          messageThreadPreviewScrollPosition: this.messageThreadPreviewRef.current?.scrollTop,
        }),
      );
    } else if (messages.length) {
      history.replace(
        WebRoutes.employeeMessagesRoute({
          orgId,
          employeeId: messages[0].employeeId,
          messageThreadPreviewScrollPosition: this.messageThreadPreviewRef.current?.scrollTop,
        }),
      );
    }
  };

  handleSendMessage =
    (contactId: number) =>
    async (
      message: string,
      type: string,
      subject?: string | null,
      sendDateTime?: Moment | null,
      optionalFields?: {
        urlFileMappings?: Record<string, string>;
        sendWithVCard?: boolean;
      },
    ) => {
      ProductTelemetryController.logEvent('clicked send employee message', {
        value: contactId,
        metadata: {
          isOnMobile: this.onMobile() ? 'true' : 'false',
          type,
          isScheduled: sendDateTime ? 'true' : 'false',
        },
      });

      const { activeEmployeeId, actions } = this.props;
      const sendVCardForOrgId = optionalFields?.sendWithVCard ? this.props.orgId : undefined;
      const result = await actions.sendMessage(
        contactId,
        employeeContextSelector(activeEmployeeId),
        message,
        type,
        subject,
        sendDateTime,
        optionalFields?.urlFileMappings,
        sendVCardForOrgId,
      );

      return result;
    };

  handleClearDraft = () => {
    const { activeEmployeeId, actions } = this.props;
    if (activeEmployeeId) {
      actions.clearSavedDraft(activeEmployeeId);
    }
  };

  handleMessageClick = (employeeId: number) => () => {
    this.showThreadForEmployeeId(employeeId);
  };

  handleNewMessageToggle = () => {
    this.setState((prev) => ({ selectingEmployee: !prev.selectingEmployee }));
  };

  handleMessageSent = (employeeId: number) => {
    this.showThreadForEmployeeId(employeeId);
    this.setState({ selectingEmployee: false });
  };

  handleEmployeeClick =
    (popupEmployeeId: number, selectedTab: string) =>
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      this.setState({ popupEmployeeId, selectedTab });
    };

  handleBackClick = () => {
    this.showThreadForEmployeeId(null);
  };

  handleMarkReadClick =
    ({
      contactId,
      messageId,
      employeeId,
    }: {
      contactId: number;
      messageId: number;
      employeeId: number;
    }) =>
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      this.props.actions.updateEmployeeMessage(contactId, messageId, employeeId, {
        isUnread: false,
      });
    };

  handleDismissMessage =
    ({
      contactId,
      messageId,
      employeeId,
    }: {
      contactId: number;
      messageId: number;
      employeeId: number;
    }) =>
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      this.props.actions.updateEmployeeMessage(contactId, messageId, employeeId, {
        isUnread: false,
        isDismissed: true,
      });
    };

  handlePopupCloseClick = () => {
    this.setState({ popupEmployeeId: null });
  };

  handleMessageTypeChange = (messageType: MessageType) => {
    this.setState({ messageType });
    if (Object.values(MESSAGE_TYPE).includes(messageType)) {
      this.handleUpdateMessage({ newType: messageType });
    }
  };

  handleSubjectChange = (newSubject: string) => {
    this.handleUpdateMessage({ newSubject });
  };

  handleTemplateSelect = (selectedSavedMessage: string) => {
    this.handleUpdateMessage({ newMessage: selectedSavedMessage });
  };

  handleUpdateMessage = ({
    newSubject,
    newType,
    newMessage,
  }: {
    newSubject?: string;
    newType?: MessageType;
    newMessage?: string;
  }) => {
    const { messageDraft, actions, activeEmployeeId } = this.props;
    const { messageType } = this.state;
    // These checks prevent us from saving a blank draft when CKEditor fires an onChange after sending the message
    if (newType && !messageDraft?.messageBody) {
      // Because you can't change the message type without typing a message first
      return;
    }
    if (!messageDraft && !newSubject && !newMessage) {
      // This prevents us from saving a blank draft when CKEditor fires an onChange after sending the message
      return;
    }
    if (!activeEmployeeId) {
      return;
    }

    const coalescedNewMessage =
      newMessage || (newMessage === undefined ? messageDraft?.messageBody : undefined);
    const coalescedNewType = newType || (newType === undefined ? messageDraft?.messageType : '');
    const coalescedNewSubject =
      newSubject || (newSubject === undefined ? messageDraft?.subject : '');

    actions.autosaveDraft(
      activeEmployeeId,
      coalescedNewMessage || '',
      coalescedNewType || messageType || MESSAGE_TYPE.TEXT,
      coalescedNewSubject,
    );
  };

  // IF (07/07/20): It's possible that the message composer gains focus and then the messages finish
  // loading - this pushes the composer out of view and starts the user at the top of the message
  // thread. That's frustrating for long threads so we scroll the composer back into view when the
  // messages load.
  handleMessageThreadLoaded = () => {
    this.messageComposerRef.current?.scrollIntoView(false);
  };

  showThreadForEmployeeId = (employeeId: number | null) => {
    const { history, orgId } = this.props;
    history.replace(
      WebRoutes.employeeMessagesRoute({
        orgId,
        employeeId,
        messageThreadPreviewScrollPosition: this.messageThreadPreviewRef.current?.scrollTop,
      }),
    );
  };

  renderMessage = (message: EmployeeOrgMessage) => {
    const { classes, width, activeEmployeeId } = this.props;
    const { selectingEmployee } = this.state;
    const selected =
      message.employeeId === activeEmployeeId && !isWidthDown('sm', width) && !selectingEmployee;
    const contactClass = classNames(classes.contact, { selected });
    const contactLinkClass = classNames(classes.contactLink, { selected });
    const cleanMessageBody = HTMLStripper.strip(message.messageBody) || '';

    return (
      <div
        role="presentation"
        key={message.messageId}
        className={`u-padding-lg ${contactClass}`}
        onClick={this.handleMessageClick(message.employeeId)}
        data-cy="employee-to-message"
      >
        <div className="u-display-flex u-justifyContent-spaceBetween u-fontWeight-500">
          <div>
            {message.nickname || message.firstName} {message.lastName}
          </div>
          <div>{moment(message.sentAt).format('M/D/YYYY')}</div>
        </div>
        <div className="u-spread">
          <div className={classes.messageBody}>
            {cleanMessageBody.length > 100
              ? `${cleanMessageBody.slice(0, 100)}...`
              : cleanMessageBody}
          </div>
          <IconButton
            className={classes.hideButton}
            size="small"
            onClick={this.handleDismissMessage(message)}
            data-cy="dismiss-message"
          >
            <MaterialDesignIcon iconName={ICON_NAMES.CLOSE} size="sm" />
          </IconButton>
        </div>
        {message.unread ? (
          <div
            role="presentation"
            onClick={this.handleMarkReadClick(message)}
            className={contactLinkClass}
          >
            Mark As Read
          </div>
        ) : null}
      </div>
    );
  };

  render() {
    const { orgId, messages, classes, employees, employeesStates, activeEmployeeId, messageDraft } =
      this.props;
    const { selectingEmployee, popupEmployeeId, messageType } = this.state;

    const employeesAreLoading = employeesStates.every(
      (requestState: RequestState) => !requestState || requestState.loading,
    );

    if (employeesAreLoading || !employees || !messages) {
      return <LoadingMessage />;
    }

    const unreadMessages = messages.filter((m) => m.unread);
    const readMessages = messages.filter((m) => !m.unread);

    const activeEmployee = employees.find((e) => e.employeeId === activeEmployeeId);

    const onSmallScreen = this.onSmallScreen();
    const contactClasses = classNames(classes.contacts, {
      [classes.mobile]: onSmallScreen,
      [classes.hidden]: onSmallScreen && (activeEmployeeId || selectingEmployee),
    });
    const threadPaneClasses = classNames(classes.threadPane, {
      [classes.mobile]: onSmallScreen,
      [classes.hidden]: onSmallScreen && !activeEmployeeId && !selectingEmployee,
    });

    const currentEmployees = employees.filter((e) => e.isCurrentEmployee);

    let canMessage;
    let enableText = false;
    let enableEmail = false;
    if (activeEmployee) {
      canMessage = new EmployeeCanMessage(activeEmployee);
      enableText = canMessage.canReceive(MESSAGE_TYPE.TEXT);
      enableEmail = canMessage.canReceive(MESSAGE_TYPE.EMAIL);
    }

    return (
      <div className={classes.container}>
        <UiBreadcrumbHeader
          pageTitle="Employee Messages"
          customPrimaryAction={
            currentEmployees.length ? (
              <UiButton
                size="sm"
                onClick={this.handleNewMessageToggle}
                variant={!selectingEmployee ? 'primary' : 'secondary'}
                dataCy="new-message"
                startIcon={!selectingEmployee ? <HeaderButtonAddIcon /> : undefined}
              >
                {!selectingEmployee ? 'New Message' : 'Cancel'}
              </UiButton>
            ) : null
          }
        />
        {messages.length === 0 && !selectingEmployee && !activeEmployee ? (
          <EmployeeMessagesEmptyState
            orgId={orgId}
            handleNewMessageToggle={this.handleNewMessageToggle}
          />
        ) : (
          <>
            <EmployeeNotificationsWarning orgId={orgId} />
            <EmployeeInvalidPhoneNumberWarning orgId={orgId} />
            <div className="u-display-flex u-flexGrow-1 u-overflow-hidden">
              <div className={contactClasses} ref={this.messageThreadPreviewRef}>
                {unreadMessages.length ? (
                  <div className={classes.categoryBanner}>
                    Unread
                    <Badge
                      badgeContent={unreadMessages.length}
                      max={10}
                      color="secondary"
                      className="u-margin-right"
                    >
                      <span />
                    </Badge>
                  </div>
                ) : null}
                {unreadMessages.map((message) => this.renderMessage(message))}
                {readMessages.length ? (
                  <div className={classes.categoryBanner}>All Messages</div>
                ) : null}
                {readMessages.map((message) => this.renderMessage(message))}
              </div>
              <div className={threadPaneClasses}>
                {!selectingEmployee && activeEmployee && activeEmployeeId ? (
                  <div className={`u-padding ${classes.threadHeader}`}>
                    <div className="u-display-flex u-alignItems-center">
                      {onSmallScreen && (
                        <MaterialDesignIcon
                          iconName={ICON_NAMES.KEYBOARD_ARROW_LEFT}
                          className="u-margin-right"
                          onClick={this.handleBackClick}
                        />
                      )}
                      <div>
                        <div className="typo-label-v2">{activeEmployee.jobTitle}</div>
                        <div
                          role="presentation"
                          className="typo-h5 typo-link-secondary"
                          onClick={this.handleEmployeeClick(activeEmployeeId, 'DETAILS')}
                        >
                          {activeEmployee.Contact.Person.firstName}
                          {activeEmployee.nickname ? ` (${activeEmployee.nickname})` : ''}{' '}
                          {activeEmployee.Contact.Person.lastName}
                        </div>
                      </div>
                    </div>
                    <EmployeeNotesButton
                      onClick={this.handleEmployeeClick(activeEmployeeId, 'COMMENTS')}
                      employeeId={activeEmployeeId}
                      className="u-margin-right-2x"
                      showCount
                    />
                  </div>
                ) : null}
                {selectingEmployee && onSmallScreen && (
                  <MaterialDesignIcon
                    iconName={ICON_NAMES.KEYBOARD_ARROW_LEFT}
                    className="u-margin-right"
                    onClick={this.handleNewMessageToggle}
                  />
                )}
                {selectingEmployee ? (
                  <EmployeeAddNewMessage
                    orgId={orgId}
                    onMessageSent={this.handleMessageSent}
                    onClose={this.handleNewMessageToggle}
                  />
                ) : activeEmployee ? (
                  <div className={`u-padding-2x ${classes.threadContainer}`}>
                    <div className={classes.thread}>
                      {canMessage ? (
                        <ContactMessageErrors canMessage={canMessage} contactType="employee" />
                      ) : null}
                      <EmployeeTextMessageList
                        employeeId={activeEmployee.employeeId}
                        onMessagesLoaded={this.handleMessageThreadLoaded}
                      />
                      {activeEmployee &&
                      activeEmployee.Contact.Person.languagePreference &&
                      activeEmployee.Contact.Person.languagePreference !== 'en' ? (
                        <MessageWidget variant="warning" canBeDismissed={false}>
                          {activeEmployee?.Contact?.Person?.languagePreference ? (
                            <div className="typo-subtitle">
                              This employee prefers to communicate in{' '}
                              {LANGUAGES[activeEmployee.Contact.Person.languagePreference]}
                            </div>
                          ) : null}
                        </MessageWidget>
                      ) : null}
                      {messageDraft?.messageBody && (
                        <div className="typo-subtitle u-textAlign-right u-width-100 u-margin-top">
                          Draft saved {messageDraft?.savedAt.format('dddd, MMMM Do, h:mm a')}
                        </div>
                      )}
                      <div ref={this.messageComposerRef} className="u-margin-top-sm">
                        <EmployeeMessageComposer
                          activeEmployee={activeEmployee}
                          message={messageDraft?.messageBody || null}
                          enableText={enableText}
                          enableEmail={enableEmail}
                          handleTemplateSelect={this.handleTemplateSelect}
                          handleSendMessageToContact={this.handleSendMessage}
                          onMessageTypeChange={this.handleMessageTypeChange}
                          orgId={orgId}
                          onClose={() => this.showThreadForEmployeeId(null)}
                          initialType={messageDraft?.messageType || MESSAGE_TYPE.TEXT}
                          subject={messageDraft?.subject}
                          onSubjectChange={this.handleSubjectChange}
                          clearDraft={this.handleClearDraft}
                        />
                      </div>

                      {(enableText || enableEmail) && (
                        <EmployeeMessageTemplates
                          employeeId={activeEmployee.employeeId}
                          orgId={orgId}
                          onSelect={this.handleTemplateSelect}
                          templateType={TEMPLATE_TYPE.MESSAGE}
                          messageType={messageType}
                        />
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
              {popupEmployeeId && (
                <EmployeeProfileDialog
                  orgId={orgId}
                  employeeId={popupEmployeeId}
                  onClose={this.handlePopupCloseClick}
                  tab={this.state.selectedTab}
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}

export const LegacyEmployeeMessages = withRouter(
  withWidth()(
    connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(EmployeeMessagesComponent)),
  ),
);
